import { useState, useEffect } from "react";
import { useGetAllStudentsQuery } from "../../../peoples/students/api/studentApi";
import { StudentResponseType } from "../../../peoples/students/types/studentResponse";
import {
  StudentAttendanceTypes,
  AttendanceApiResponse,
} from "../types/attendanceTypes";
import { API_CONFIG } from "../../../../core/config";
import {
  useGetAllAttendanceQuery,
  useCreateAttendanceMutation,
  useUpdateAttendanceMutation,
  useCreateBulkAttendanceMutation,
  useUpdateBulkAttendanceMutation,
} from "../api/attendanceApi";
import { showToast } from "../../../../core/utils/alert";
import { PaginationProps } from "../../../../core/data/interface";
import { useBatchTiming } from "../../../configuration/batch-timing/hooks/useBatchTiming";

export const useAttendance = (params?: PaginationProps) => {
  const currentDate = new Date().toISOString().split("T")[0];
  const [selectedStudentKeys, setSelectedStudentKeys] = useState<React.Key[]>(
    []
  );
  const [bulkStatus, setBulkStatus] = useState("Present");
  const [filterDate, setFilterDate] = useState(currentDate);
  const [students, setStudents] = useState<StudentAttendanceTypes[]>([]);
  const [allAttendanceRecords, setAllAttendanceRecords] = useState<
    AttendanceApiResponse[]
  >([]);
  const [filters, setFilters] = useState<any>({});
  const [tempFilters, setTempFilters] = useState<any>({});
  const [selectedCourseStages, setSelectedCourseStages] = useState<any[]>([]);
  const [sortConfig, setSortConfig] = useState<any>({});

  const { data: studentData, isLoading } = useGetAllStudentsQuery({
    page: params?.page || 1,
    size: params?.size || 10,
    search: params?.search || "",
    ...filters,
    ...sortConfig,
  });

  const { data: initialAttendanceData } = useGetAllAttendanceQuery({
    date: filterDate,
    page: 1,
    size: 100,
  });

  const { batchTimings } = useBatchTiming();

  useEffect(() => {
    const fetchAllAttendance = async () => {
      if (!initialAttendanceData?.data) return;

      const totalPages = Math.ceil(
        (initialAttendanceData.data.totalItems || 0) / 100
      );
      let allRecords: AttendanceApiResponse[] = [];

      try {
        if (initialAttendanceData.data.items) {
          allRecords = [...initialAttendanceData.data.items];
        }

        for (let page = 2; page <= totalPages; page++) {
          const response = await fetch(
            `${API_CONFIG.BASE_URL}student-attendance/get-all?date=${filterDate}&page=${page}&size=100`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
                "Content-Type": "application/json",
              },
            }
          );
          const data = await response.json();
          if (data.success && data.data.items) {
            allRecords = [...allRecords, ...data.data.items];
          }
        }

        setAllAttendanceRecords(allRecords);
      } catch (error) {
        console.error("Error fetching attendance:", error);
      }
    };

    fetchAllAttendance();
  }, [filterDate, initialAttendanceData?.data]);

  useEffect(() => {
    if (studentData?.data) {
      const studentItems = studentData.data.items || [];

      const updatedStudents: StudentAttendanceTypes[] = studentItems
        .filter((student) => student.status !== "inactive")
        .map((student: StudentResponseType) => {
          const existingAttendance = allAttendanceRecords.find(
            (a) => a.student_id === student.id && a.date === filterDate
          );

          const photoUrl = student.docs.photo
            ? `${API_CONFIG.BASE_URL}students/get-thumbnail/${student.docs.photo
            }?v=${Date.now()}`
            : student.profile.gender?.toLowerCase() === "female"
              ? "assets/img/profiles/avatar-03.jpg"
              : "assets/img/profiles/avatar-02.jpg";

          let attendanceStatus = "";
          if (existingAttendance) {
            attendanceStatus =
              existingAttendance.attendance_status.charAt(0).toUpperCase() +
              existingAttendance.attendance_status.slice(1).toLowerCase();
          }

          const levelName = student.course?.stage?.name || "-";

          return {
            id: existingAttendance?.id || student.id,
            studentId: student.profile.studentid || "",
            name: `${student.first_name} ${student.last_name}`,
            course: student.course?.name || "",
            level: levelName,
            attendanceDate: filterDate,
            attendanceStatus,
            student: {
              id: student.id,
              first_name: student.first_name,
              last_name: student.last_name,
              status: student.status,
              center: student.center
                ? {
                  id: student.center.id,
                  name: student.center.name,
                  code: student.center.code,
                  zone: student.center.zone,
                  district: student.center.district,
                  status: student.center.status,
                }
                : undefined,
            },
            img: photoUrl,
            gender: student.profile.gender || "male",
            key: student.id,
          };
        });

      setStudents(updatedStudents);
    }
  }, [studentData, allAttendanceRecords, filterDate, batchTimings]);

  const [createAttendance] = useCreateAttendanceMutation();
  const [updateAttendance] = useUpdateAttendanceMutation();
  const [createBulkAttendance] = useCreateBulkAttendanceMutation();
  const [updateBulkAttendance] = useUpdateBulkAttendanceMutation();

  const handleOptionChange = async (
    studentKey: React.Key,
    newStatus: string
  ) => {
    try {
      const student = students.find((s) => s.key === studentKey);
      if (!student) return;

      const existingAttendance = allAttendanceRecords.find(
        (a) => a.student_id === Number(studentKey) && a.date === filterDate
      );

      if (filterDate < currentDate && existingAttendance?.attendance_status) {
        showToast({
          title: "Error",
          text: "Cannot update attendance for past dates.",
          icon: "error",
          timer: 5000,
        });
        return;
      }

      if (existingAttendance) {
        await updateAttendance({
          id: existingAttendance.id,
          student_id: Number(studentKey),
          attendance_status: newStatus.toLowerCase(),
          date: filterDate,
        }).unwrap();

        showToast({
          title: "Success",
          text: "Attendance updated successfully",
          icon: "success",
          timer: 5000,
        });
      } else {
        await createAttendance({
          student_id: Number(studentKey),
          attendance_status: newStatus.toLowerCase(),
          date: filterDate,
        }).unwrap();

        showToast({
          title: "Success",
          text: "Attendance marked successfully",
          icon: "success",
          timer: 5000,
        });
      }

      setStudents((prevStudents) =>
        prevStudents.map((s) => {
          if (s.key === studentKey) {
            return {
              ...s,
              attendanceStatus:
                newStatus.charAt(0).toUpperCase() +
                newStatus.slice(1).toLowerCase(),
            };
          }
          return s;
        })
      );
    } catch (error) {
      console.error("Failed to update attendance:", error);
      showToast({
        title: "Error",
        text: "Failed to update attendance. Please try again.",
        icon: "error",
        timer: 5000,
      });
    }
  };

  const handleBulkStatusChange = async () => {
    if (selectedStudentKeys.length === 0 || !bulkStatus) return;

    try {
      const selectedStudents = students.filter((student) =>
        selectedStudentKeys.includes(student.key)
      );

      const hasPastDateAttendance = selectedStudents.some((student) => {
        const existingAttendance = allAttendanceRecords.find(
          (a) => a.student_id === student.key && a.date === filterDate
        );
        return (
          filterDate < currentDate && existingAttendance?.attendance_status
        );
      });

      if (hasPastDateAttendance) {
        showToast({
          title: "Error",
          text: "Cannot update attendance for past dates.",
          icon: "error",
          timer: 5000,
        });
        return;
      }

      const studentsToUpdate: Array<{
        id: number;
        student_id: number;
        attendance_status: string;
        date: string;
      }> = [];

      const studentsToCreate = selectedStudents.filter((student) => {
        const existingAttendance = allAttendanceRecords.find(
          (a) => a.student_id === student.key && a.date === filterDate
        );
        return !existingAttendance;
      });

      selectedStudents.forEach((student) => {
        const existingAttendance = allAttendanceRecords.find(
          (a) => a.student_id === student.key && a.date === filterDate
        );

        if (existingAttendance) {
          studentsToUpdate.push({
            id: existingAttendance.id,
            student_id: student.key,
            attendance_status: bulkStatus.toLowerCase(),
            date: filterDate,
          });
        }
      });

      if (studentsToUpdate.length > 0) {
        await updateBulkAttendance({
          attendance: studentsToUpdate,
        }).unwrap();
      }

      if (studentsToCreate.length > 0) {
        await createBulkAttendance({
          attendance: studentsToCreate.map((student) => ({
            student_id: student.key,
            attendance_status: bulkStatus.toLowerCase(),
            date: filterDate,
          })),
        }).unwrap();
      }

      showToast({
        title: "Success",
        text: `Attendance ${studentsToUpdate.length > 0 && studentsToCreate.length > 0
          ? "updated and marked"
          : studentsToUpdate.length > 0
            ? "updated"
            : "marked"
          } for ${selectedStudentKeys.length} students`,
        icon: "success",
        timer: 5000,
      });

      setStudents((prevStudents) =>
        prevStudents.map((student) => {
          if (selectedStudentKeys.includes(student.key)) {
            return {
              ...student,
              attendanceStatus:
                bulkStatus.charAt(0).toUpperCase() +
                bulkStatus.slice(1).toLowerCase(),
            };
          }
          return student;
        })
      );

      setSelectedStudentKeys([]);
    } catch (error) {
      console.error("Failed to update bulk attendance:", error);
      showToast({
        title: "Error",
        text: "Failed to update bulk attendance. Please try again.",
        icon: "error",
        timer: 5000,
      });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "present":
        return "bg-success-transparent text-success";
      case "ncns":
        return "bg-warning-transparent text-warning";
      case "dropout":
        return "bg-info-transparent text-info";
      case "leave":
        return "bg-dark-transparent text-dark";
      case "absent":
        return "bg-danger-transparent text-danger";
      default:
        return "";
    }
  };

  const getInitialAttendanceStatus = (status: string) => {
    return status ? status.toLowerCase() : "";
  };

  return {
    students,
    isLoading,
    selectedStudentKeys,
    bulkStatus,
    filterDate,
    currentDate,
    paginationData: {
      totalPages: studentData?.data?.totalPages || 1,
      pageSize: studentData?.data?.pageSize || 10,
      totalItems: studentData?.data?.totalItems || 0,
      currentPage: studentData?.data?.currentPage || 1,
    },
    setSelectedStudentKeys,
    setBulkStatus,
    setFilterDate,
    handleOptionChange,
    handleBulkStatusChange,
    getStatusColor,
    getInitialAttendanceStatus,
    filters,
    tempFilters,
    setTempFilters,
    handleFilterChange: (filterName: string, value: any, courses?: any[]) => {
      setTempFilters((prev: any) => ({
        ...prev,
        [filterName]: filterName === 'status' ? value?.value : (value?.value ? Number(value.value) : undefined)
      }));

      if (filterName === 'course_id' && courses) {
        const selectedCourse = courses.find((course: any) => course.id.toString() === value?.value);
        setSelectedCourseStages(selectedCourse?.courseStages || []);
        setTempFilters((prev: any) => ({
          ...prev,
          level_id: undefined
        }));
      }
    },
    handleApplyFilters: () => {
      setFilters(tempFilters);
    },
    handleResetFilters: () => {
      const emptyFilters = {
        center_id: undefined,
        course_id: undefined,
        batch_id: undefined,
        status: undefined,
        session_id: undefined,
        level_id: undefined,
      };
      setFilters(emptyFilters);
      setTempFilters(emptyFilters);
      setSortConfig({});
    },
    selectedCourseStages,
    sortConfig,
    handleSortChange: (sortBy: string, order: 'asc' | 'desc') => {
      const newOrder = sortConfig.sort_by === sortBy && sortConfig.sort_order === 'asc' ? 'desc' : 'asc';
      setSortConfig({
        sort_by: sortBy,
        sort_order: newOrder
      });
    },
  };
};
